import {
  Box,
  Button,
  Header,
  SpaceBetween,
  Container,
  FormField,
  Autosuggest,
  Grid,
  Input,
  Link,
  Textarea,
  ColumnLayout,
  Spinner,
  TextContent,
} from '@amzn/awsui-components-console';
import React, { useState, useEffect } from 'react';
import { LaunchTask } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { convertToPT } from '../utils';
import { getEnvironment } from '../../../authentication/config';

// The Launch Accelerator (ALA) UI is stage-specific: FLC Alpha points at the ALA
// Gamma UI, FLC Integ points at ALA Prod. The path segment (/alm/launches/) is the
// ALA UI's own internal route and is the same in both environments.
const ALA_BASE_URL: Record<ReturnType<typeof getEnvironment>, string> = {
  alpha: 'https://gamma.regions.aws.dev',
  gamma: 'https://gamma.regions.aws.dev',
  integ: 'https://regions.aws.dev',
};

interface detailSectionProps {
  launchTask: LaunchTask;
  id: string;
  loading: boolean;
}

export const DetailSection = ({ launchTask, loading }: detailSectionProps) => {
  //const { isAuthenticated, token, packageInfoPermissionsGroup } = useAuthState();
  const [dataLoading, setDataLoading] = useState(true);

  const [launchTaskDetail, setLaunchTaskDetail] =
    useState<LaunchTask>(launchTask);

  useEffect(() => {
    setLaunchTaskDetail(launchTask);
    setDataLoading(loading);
  }, [launchTask]);

  if (loading) {
    return (
      <Container>
        <Spinner size='large' />
      </Container>
    );
  }

  return (
    <SpaceBetween direction='vertical' size='l'>
      <Container>
        <Grid
          gridDefinition={[
            { colspan: 12 },
            { colspan: 2 },
            { colspan: 2 },
            { colspan: 2 },
            { colspan: 2 },
            { colspan: 2 },
            { colspan: 2 },
            { colspan: 4 },
            { colspan: 4 },
            { colspan: 4 },
          ]}
        >
          {/* Launch Name spans the full width of the section so long names
              aren't truncated in a narrow column. */}
          <FormField label='Launch name'>
            <Input
              readOnly={true}
              disabled={true}
              value={launchTaskDetail?.name ? launchTaskDetail.name : '-'}
            />
          </FormField>

          <div className='column1'>
            <SpaceBetween direction='vertical' size='xxl'>
              <FormField
                // description='This is a description.'
                label='Launch ID'
              >
                <Input
                  readOnly={true}
                  disabled={true}
                  value={
                    launchTaskDetail?.launchId ? launchTaskDetail.launchId : '-'
                  }
                />
              </FormField>
              {launchTaskDetail?.launchId && !launchTaskDetail?.parentTask && (
                <FormField label='ALA entry'>
                  <Link
                    external
                    href={`${ALA_BASE_URL[getEnvironment()]}/alm/launches/${
                      launchTaskDetail.launchId
                    }`}
                  >
                    View in ALA
                  </Link>
                </FormField>
              )}
            </SpaceBetween>
          </div>

          <div className='column2'>
            <SpaceBetween direction='vertical' size='xxl'>
              <FormField label='Launch conference'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={
                    launchTaskDetail?.conference
                      ? launchTaskDetail.conference
                      : '-'
                  }
                />
              </FormField>
              <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                <FormField label='Launch type'>
                  <Input
                    readOnly={true}
                    disabled={true}
                    value={launchTaskDetail?.type ? launchTaskDetail.type : '-'}
                  />
                </FormField>
                <FormField label='Scope'>
                  <Input
                    readOnly={true}
                    disabled={true}
                    value={
                      launchTaskDetail?.scope ? launchTaskDetail.scope : '-'
                    }
                  />
                </FormField>
              </Grid>
            </SpaceBetween>
          </div>

          <div className='column3'>
            <SpaceBetween direction='vertical' size='xxl'>
              <FormField label='Launch venue'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={launchTaskDetail?.venue ? launchTaskDetail.venue : '-'}
                />
              </FormField>
              <FormField label='Code name'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={
                    launchTaskDetail?.codeName ? launchTaskDetail.codeName : '-'
                  }
                />
              </FormField>
            </SpaceBetween>
          </div>

          <div className='column4'>
            <SpaceBetween direction='vertical' size='xxl'>
              <FormField label='Product name'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={
                    launchTaskDetail?.productName
                      ? launchTaskDetail.productName
                      : '-'
                  }
                />
              </FormField>
              <FormField label='Product manager'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={
                    launchTaskDetail?.productManager
                      ? launchTaskDetail.productManager
                      : '-'
                  }
                />
              </FormField>
            </SpaceBetween>
          </div>

          <div className='column5'>
            <SpaceBetween direction='vertical' size='xxl'>
              <FormField label='Launch date'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={
                    launchTaskDetail?.launchDate
                      ? convertToPT(launchTaskDetail.launchDate)
                      : '-'
                  }
                />
              </FormField>
              <FormField label='Marketing lead'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={
                    launchTaskDetail?.marketingLead
                      ? launchTaskDetail.marketingLead
                      : '-'
                  }
                />
              </FormField>
            </SpaceBetween>
          </div>

          <div className='column6'>
            <SpaceBetween direction='vertical' size='xxl'>
              <FormField label='Launch status'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={
                    launchTaskDetail?.status ? launchTaskDetail.status : '-'
                  }
                />
              </FormField>
              <FormField label='Tech docs needed'>
                <Input
                  readOnly={true}
                  disabled={true}
                  value={launchTaskDetail?.techDocsNeeded ? 'Yes' : 'No'}
                />
              </FormField>
            </SpaceBetween>
          </div>

          <FormField label='Final build deadline (ELTB)'>
            <Input
              value={
                launchTask?.lastTimeToBuild
                  ? convertToPT(launchTask?.lastTimeToBuild, true)
                  : '-'
              }
              readOnly={true}
              disabled={true}
            />
          </FormField>
          <FormField label='Target live time'>
            <Input
              value={
                launchTask?.targetDocLiveTime
                  ? convertToPT(launchTask?.targetDocLiveTime, true)
                  : '-'
              }
              readOnly={true}
              disabled={true}
            />
          </FormField>
          <FormField label='Resolve venue time'>
            <Input
              value={
                launchTask?.resolveVenueTime
                  ? convertToPT(launchTask?.resolveVenueTime, true)
                  : '-'
              }
              readOnly={true}
              disabled={true}
            />
          </FormField>
        </Grid>
      </Container>
    </SpaceBetween>
  );
};

export default DetailSection;
